import React, {useEffect, useRef, useState} from 'react';
import {motion, useMotionValue, useAnimationFrame, useTransform} from 'framer-motion';

const Carousel = ({items, speed = 30, autoplay = true, onItemClick, variant = 'default'}) => {
    const x = useMotionValue(0);
    const baseX = useMotionValue(0);
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateWidth = () => {
            const totalWidth = container.scrollWidth / 2;
            setContainerWidth(totalWidth);
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    useAnimationFrame((_, delta) => {
        if (autoplay && !isDragging) {
            const moveBy = (delta / 1000) * speed;
            baseX.set(baseX.get() - moveBy);
        }
    });

    const wrappedX = useTransform(baseX, (v) => {
        if (!containerWidth) return v;
        return ((v % -containerWidth) + -containerWidth) % -containerWidth;
    });

    const isImageSquare = variant === 'imageSquare';

    return (
        <div className="mx-0 mt-6 overflow-hidden pr-3">
            <div className="relative flex overflow-hidden" ref={containerRef}>
                <motion.div
                    style={{x: wrappedX}}
                    drag="x"
                    dragConstraints={{left: -Infinity, right: 0}}
                    dragElastic={0}
                    className="flex min-w-fit"
                >
                    {items && [...items, ...items].map((item, index) => (
                        <div
                            key={index}
                            className={
                                isImageSquare
                                    ? `flex items-center justify-center mr-4 w-20 h-20 rounded-md bg-[#F2F2F2] ${onItemClick ? 'cursor-pointer' : ''}`
                                    : `flex items-center mr-[2rem] gap-4 px-6 py-1 lg:py-2 rounded-[50px] bg-[#F2F2F2] ${onItemClick ? 'cursor-pointer' : ''}`
                            }
                            role={onItemClick ? 'button' : undefined}
                            tabIndex={onItemClick ? 0 : undefined}
                            onClick={onItemClick ? () => onItemClick(item, index % (items?.length || 1)) : undefined}
                        >
                            <img
                                src={item.icon}
                                alt={item.title}
                                width={isImageSquare ? 60 : 20}
                                height={isImageSquare ? 60 : 20}
                                className={`pointer-events-none select-none ${isImageSquare ? 'object-contain' : 'grayscale'}`}
                                draggable={false}
                            />
                            {!isImageSquare && (
                                <span className="whitespace-nowrap text-[#838383] text-[clamp(10px,0.97vw,14px)] pr-6">
                                  {item.title}
                                </span>
                            )}
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default Carousel;
